/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useGSAP, useGSAPConfig } from '@gsap/react';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import React, { useEffect, useRef, useState } from 'react';
import { BackgroundColorTheme } from '../../templates/Page';
import { clsx } from '../../utils/utils';
import * as styles from './Background.module.scss';
import * as layoutStyles from './Layout.module.scss';
import ScrollingItems from './ScrollingItems';
import * as experiencesModuleStyles from './modules/ExperiencesModule.module.scss';

import { createPortal } from 'react-dom';
import frontCloud from '../../images/layout_cloud_front.webp';
import middleRightCloud from '../../images/layout_cloud_middleRight.webp';
import topLeftCloud from '../../images/layout_cloud_topLeft.webp';
import topRightCloud from '../../images/layout_cloud_topRight.webp';
import moonImage from '../../images/moon.webp';
import starsImage from '../../images/stars.webp';

const N_THEMES = 5;
const MIN_TIME_BETWEEN_TWEENS_MS = 100;
const DAY_TO_NIGHT_TRANSITION_HEIGHT = 500;
const SUNSET_FIRST_PART_FRACTION = 10;
const DAY_TO_NIGHT_TRANSITION_INDEX = 3;
const GRADIENT_GREY_AREA_START = 0.42;
const GRADIENT_GREY_AREA_END = 0.58;

const BACKGROUND_GRADIENTS = {
  panoramic:
    'linear-gradient(to bottom, #8bd5ff 0%, #87d4fb 30%, #84d3f8 50%, #afe5fb 75%, #daf7ff 100%)',
  evening:
    'linear-gradient(to bottom, #5c9dd2 0%, #7eb4d9 30%, #95c3dd 50%, #b0cbd3 75%, #fdc698 100%)',
  sunset:
    'linear-gradient(to bottom, #80b5d2 0%, #bfc0b0 30%, #dac4a1 50%, #feca8e 75%, #ff7932 100%)',
  moonlit:
    'linear-gradient(to bottom, #1e3b58 0%, #25466f 30%, #294d7e 50%, #2f5692 75%, #436598 100%)',
  starlit:
    'linear-gradient(to bottom, #142b41 0%, #19304b 30%, #1d3351 50%, #21385a 75%, #2d4a75 100%)',
  darknight:
    'linear-gradient(to bottom, #142b41 0%, #19304b 30%, #1d3351 50%, #21385a 75%, #2d4a75 100%)',
};

export const CLOUDS_KEYFRAMES = [
  {
    filter: 'sepia(0) hue-rotate(0deg) saturate(0) contrast(0.15) brightness(1.76)',
    duration: 0,
    immediateRender: true,
  },
  {
    // tween to evening
    filter: 'sepia(0.5) hue-rotate(-45deg) saturate(3.5) contrast(0.15) brightness(1.76)',
    duration: 1,
  },
  {
    // tween to sunset
    filter: 'sepia(0.2) hue-rotate(-24deg) saturate(8.5) contrast(0.45) brightness(1.38)',
    duration: 1,
  },
  {
    // half tween to moonlit, remove saturation to hide hue rotation
    filter: 'sepia(0.2) hue-rotate(-24deg) saturate(0) contrast(0.65) brightness(1.14)',
    duration: 0.5,
  },
  {
    // set moonlit hue rotation
    filter: 'sepia(0.2) hue-rotate(188deg) saturate(0) contrast(0.65) brightness(1.14)',
    duration: 0,
  },
  {
    // half tween to moonlit, add back saturation
    filter: 'sepia(0.2) hue-rotate(188deg) saturate(8.5) contrast(1.05) brightness(0.7)',
    duration: 0.5,
  },
  {
    // tween to starlit
    filter: 'sepia(0.2) hue-rotate(194deg) saturate(11.5) contrast(1.12) brightness(0.5)',
    duration: 1,
  },
];

function getThemeSelector(theme: BackgroundColorTheme) {
  return `.${layoutStyles.main}[data-theme="${theme}"], .${layoutStyles.content} > [data-theme="${theme}"], .${experiencesModuleStyles.experienceStableWrapper}[data-theme="${theme}"]`;
}

function getThemeStartY(theme: BackgroundColorTheme): number | null {
  const el = document.querySelector(getThemeSelector(theme));
  if (!el) {
    return null;
  }
  const rect = el.getBoundingClientRect();
  let topY = window.scrollY + rect.top - 100;
  if (rect.height <= window.innerHeight) {
    topY += (rect.height - window.innerHeight) / 2;
  }
  return gsap.utils.clamp(0, document.body.scrollHeight, topY);
}

function getAnimationPositionFromScroll(options: { safeTransitions?: boolean } = {}) {
  options = {
    safeTransitions: true,
    ...options,
  };
  const panoramicThemeY = getThemeStartY('panoramic');
  const eveningThemeY = getThemeStartY('evening');
  const sunsetThemeY = getThemeStartY('sunset');
  const moonlitThemeY = getThemeStartY('moonlit');
  const starlitThemeY = getThemeStartY('starlit');
  const documentHeight = document.body.scrollHeight;

  const ysWithNulls: Array<number | null> = [
    panoramicThemeY,
    eveningThemeY,
    sunsetThemeY,
    moonlitThemeY,
    starlitThemeY,
    documentHeight,
  ];
  const firstNotNullYIndex = ysWithNulls.findIndex(n => n !== null);
  // Fill nulls: before first visible theme → -1 (already passed).
  // Between visible themes → next non-null value (skip missing theme).
  // After last visible theme → documentHeight (unreachable).
  const ys = ysWithNulls.map((y, i) => {
    if (y !== null) return y;
    if (i < firstNotNullYIndex) return -1;
    // Find the next non-null value
    for (let j = i + 1; j < ysWithNulls.length; j++) {
      if (ysWithNulls[j] !== null) return ysWithNulls[j]!;
    }
    return documentHeight;
  });

  let animationPosition = 0;
  for (let i = 1; i < ys.length; i++) {
    const y = ys[i];
    if (window.scrollY > y) {
      animationPosition += 1 / N_THEMES;
    } else {
      let constBase = 0;
      let mult;
      let startInterval;
      let endInterval;
      let ease = (n: number) => n;
      if (options.safeTransitions && i === DAY_TO_NIGHT_TRANSITION_INDEX) {
        if (window.scrollY < y - DAY_TO_NIGHT_TRANSITION_HEIGHT) {
          mult = 100 / N_THEMES / SUNSET_FIRST_PART_FRACTION;
          startInterval = ys[i - 1];
          endInterval = y - DAY_TO_NIGHT_TRANSITION_HEIGHT;
        } else {
          constBase = 100 / N_THEMES / SUNSET_FIRST_PART_FRACTION;
          mult = (100 / N_THEMES) * ((SUNSET_FIRST_PART_FRACTION - 1) / SUNSET_FIRST_PART_FRACTION);
          startInterval = y - DAY_TO_NIGHT_TRANSITION_HEIGHT;
          endInterval = y;
        }
      } else {
        mult = 100 / N_THEMES;
        startInterval = ys[i - 1];
        endInterval = y;
        if (options.safeTransitions) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          ease = CustomEase.create(
            'custom',
            'M0,0 C0,0 0.664,-0.025 0.768,0.612 0.829,0.988 1,1 1,1 ',
          );
        }
      }
      animationPosition +=
        (constBase +
          mult * ease((window.scrollY - startInterval) / (endInterval - startInterval))) /
        100;
      break;
    }
  }
  return gsap.utils.clamp(0, 1, animationPosition);
}

export function getAnimationSafePositionFromScroll() {
  const animationPosition = getAnimationPositionFromScroll();
  const gradientGreyAreaMiddle = (GRADIENT_GREY_AREA_END + GRADIENT_GREY_AREA_START) / 2;
  if (GRADIENT_GREY_AREA_START < animationPosition && animationPosition <= gradientGreyAreaMiddle) {
    return GRADIENT_GREY_AREA_START;
  } else if (
    gradientGreyAreaMiddle < animationPosition &&
    animationPosition < GRADIENT_GREY_AREA_END
  ) {
    return GRADIENT_GREY_AREA_END;
  } else {
    return animationPosition;
  }
}

export function useThemeFromScroll() {
  const [currentTheme, setCurrentTheme] = useState<BackgroundColorTheme | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const animationPosition = getAnimationPositionFromScroll();
      const themes = ['panoramic', 'evening', 'sunset', 'moonlit', 'starlit'] as const;
      const clampedIndex = gsap.utils.clamp(
        0,
        themes.length - 1,
        Math.floor((animationPosition + 0.1) * themes.length),
      );
      const theme = themes[clampedIndex];
      if (theme !== currentTheme) {
        setCurrentTheme(theme);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentTheme]);

  return currentTheme;
}

export function useScrollLinkedTimeline(
  tlRef: React.MutableRefObject<gsap.core.Timeline | null>,
  options: { safeTransitions?: boolean; duration?: number } = {},
) {
  options = {
    safeTransitions: true,
    duration: 0.4,
    ...options,
  };
  const lastTweenTime = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!tlRef.current) {
        return;
      }
      const now = Date.now();
      if (now - lastTweenTime.current > MIN_TIME_BETWEEN_TWEENS_MS) {
        tlRef.current.tweenTo(
          (options.safeTransitions
            ? getAnimationSafePositionFromScroll()
            : getAnimationPositionFromScroll({ safeTransitions: false })) * N_THEMES,
          {
            duration: options.duration,
          },
        );
        lastTweenTime.current = now;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
}

export function useScrollLinkedClouds(selector: string, gsapConfig: useGSAPConfig) {
  const scrollAnimationsTl = useRef<gsap.core.Timeline | null>(null);
  useGSAP(() => {
    scrollAnimationsTl.current = gsap.timeline();
    scrollAnimationsTl.current.to(selector, { keyframes: CLOUDS_KEYFRAMES });
    scrollAnimationsTl.current.progress(getAnimationSafePositionFromScroll());
    scrollAnimationsTl.current.pause();
  }, gsapConfig);
  useScrollLinkedTimeline(scrollAnimationsTl);
}

export interface BackgroundProps {
  className?: string;
  withTopClouds?: boolean;
  withBottomClouds?: boolean;
}

const Background = ({
  className,
  withTopClouds,
  withBottomClouds,
}: BackgroundProps): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const backgroundAndCloudsTl = useRef<gsap.core.Timeline | null>(null);
  const sunMoonAndStarsTl = useRef<gsap.core.Timeline | null>(null);
  const [mainEl, setMainElRef] = useState<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      backgroundAndCloudsTl.current = gsap.timeline();
      sunMoonAndStarsTl.current = gsap.timeline();

      // BACKGROUND GRADIENT
      const bgTimeline = gsap.timeline();
      bgTimeline.to(`.${styles.background}`, {
        keyframes: [
          { background: BACKGROUND_GRADIENTS.panoramic, duration: 0, immediateRender: true },
          { background: BACKGROUND_GRADIENTS.evening, duration: 1 },
          { background: BACKGROUND_GRADIENTS.sunset, duration: 1 },
          { background: BACKGROUND_GRADIENTS.moonlit, duration: 1 },
          { background: BACKGROUND_GRADIENTS.starlit, duration: 1 },
          // Extra theme to give starlit one second of duration,
          // and some subtle animation
          { background: BACKGROUND_GRADIENTS.darknight, duration: 1 },
        ],
      });
      backgroundAndCloudsTl.current.add(bgTimeline, 0);

      // CLOUDS
      if (withTopClouds) {
        const cloudsTimeline = gsap.timeline();
        cloudsTimeline.to(
          `.${styles.topLeftCloud}, .${styles.topRightCloud}, .${styles.middleRightCloud}`,
          { keyframes: CLOUDS_KEYFRAMES },
        );
        backgroundAndCloudsTl.current.add(cloudsTimeline, 0);
      }

      // BACKGROUND TIMELINE START POSITION
      const bgAnimationPosition = getAnimationPositionFromScroll();
      setTimeout(() => {
        const bgAnimationPosition = getAnimationPositionFromScroll();
        backgroundAndCloudsTl.current!.progress(bgAnimationPosition);
      }, 0);
      backgroundAndCloudsTl.current.progress(bgAnimationPosition);
      backgroundAndCloudsTl.current.pause();

      // SUN
      gsap.matchMedia().add(
        {
          isDesktop: '(min-width: 1200px)',
          isTablet: '(min-width: 720px) and (max-width: 1199px)',
          isMobile: '(max-width: 719px)',
        },
        context => {
          const { isTablet, isDesktop } = context.conditions || {};
          const sunTimeline = gsap.timeline();
          sunTimeline.to(`.${styles.sun}`, {
            keyframes: [
              {
                yPercent: -50,
                y: 0,
                immediateRender: true,
                duration: 0,
              },
              {
                yPercent: 35,
                y: '160vh',
                duration: 4,
              },
              {
                autoAlpha: 0,
                duration: 0,
              },
            ],
          });
          sunTimeline.to(
            `.${styles.sun}`,
            {
              keyframes: [
                {
                  xPercent: isDesktop ? -70 : -90,
                  duration: 1,
                  ease: 'power1.out',
                },
                {
                  xPercent: isDesktop ? -60 : -60,
                  background: isDesktop ? undefined : 'rgb(255, 245, 180)',
                  filter: 'blur(15px)',
                  duration: 1,
                  ease: 'power2.in',
                },
              ],
            },
            '<',
          );
          sunTimeline.to(
            `.${styles.sunAura}`,
            {
              autoAlpha: 1,
              duration: 2,
              ease: 'power1.in',
            },
            '<',
          );
          sunMoonAndStarsTl.current!.add(sunTimeline, 0);
        },
      );

      // MOON
      gsap.matchMedia().add(
        {
          isDesktop: '(min-width: 1200px)',
          isTablet: '(min-width: 720px) and (max-width: 1199px)',
          isMobile: '(max-width: 719px)',
        },
        context => {
          const { isTablet, isDesktop } = context.conditions || {};
          const moonTimeline = gsap.timeline();
          moonTimeline.to(`.${styles.moon}`, {
            keyframes: [
              {
                autoAlpha: 1,
                xPercent: 100,
                yPercent: 0,
                immediateRender: true,
                delay: 2.7,
                duration: 0,
              },
              {
                xPercent: isDesktop ? 0 : isTablet ? 50 : 65,
                yPercent: -100,
                duration: 1.3,
              },
              {
                autoAlpha: 0,
                duration: 0,
              },
            ],
          });
          sunMoonAndStarsTl.current!.add(moonTimeline, 0);
        },
      );

      // STARS
      const starsTimeline = gsap.timeline();
      starsTimeline.from(`.${styles.stars}`, {
        autoAlpha: 0,
        duration: 1,
      });
      starsTimeline.to(
        `.${styles.stars}`,
        {
          scale: 1.2,
          duration: 2,
        },
        '<',
      );
      sunMoonAndStarsTl.current.add(starsTimeline, 2.8);

      // SUN/MOON/STARS TIMELINE START POSITION
      const sunAndMoonAnimationPosition = getAnimationPositionFromScroll({
        safeTransitions: false,
      });
      setTimeout(() => {
        const sunAndMoonAnimationPosition = getAnimationPositionFromScroll({
          safeTransitions: false,
        });
        sunMoonAndStarsTl.current!.progress(sunAndMoonAnimationPosition);
      }, 0);
      sunMoonAndStarsTl.current.progress(sunAndMoonAnimationPosition);
      sunMoonAndStarsTl.current.pause();
    },
    { scope: containerRef },
  );

  useGSAP(
    () => {
      if (mainEl && withBottomClouds) {
        const cloudsTimeline = gsap.timeline();
        cloudsTimeline.to(`.${styles.frontCloud}`, { keyframes: CLOUDS_KEYFRAMES });
        backgroundAndCloudsTl.current!.add(cloudsTimeline, 0);
      }
    },
    { dependencies: [mainEl] },
  );

  useScrollLinkedTimeline(backgroundAndCloudsTl);
  useScrollLinkedTimeline(sunMoonAndStarsTl, { safeTransitions: false, duration: 0.4 });

  useEffect(() => {
    setMainElRef(document.querySelector<HTMLDivElement>('div[role="main"]'));
  }, []);

  return (
    <div ref={containerRef} className={clsx(className, styles.container)}>
      <div className={styles.background}></div>
      <div className={styles.sun}>
        <div className={styles.sunAura}></div>
      </div>
      <img src={moonImage} className={styles.moon} alt="" loading="lazy" />
      <img src={starsImage} className={styles.stars} alt="" loading="lazy" />
      {withTopClouds && (
        <div className={styles.backCloudsContainer}>
          <ScrollingItems animationDuration={400} className={styles.topLeftCloudContainer}>
            <img src={topLeftCloud} className={styles.topLeftCloud} alt="" loading="lazy" />
          </ScrollingItems>
          <ScrollingItems
            animationDuration={600}
            className={styles.topRightCloudContainer}
            elementsClassName={styles.topRightCloudElementsContainer}
          >
            <img src={topRightCloud} className={styles.topRightCloud} alt="" loading="lazy" />
          </ScrollingItems>
          <ScrollingItems
            animationDuration={300}
            className={styles.middleRightCloudContainer}
            elementsClassName={styles.middleRightCloudElementsContainer}
          >
            <img src={middleRightCloud} className={styles.middleRightCloud} alt="" loading="lazy" />
          </ScrollingItems>
        </div>
      )}
      {withBottomClouds &&
        mainEl &&
        createPortal(
          <div className={styles.frontCloudsContainer}>
            <ScrollingItems animationDuration={400} className={styles.frontCloudContainer}>
              <img src={frontCloud} className={styles.frontCloud} alt="" loading="lazy" />
            </ScrollingItems>
          </div>,
          mainEl,
        )}
    </div>
  );
};

export default Background;
